import { z } from 'zod';
import { locationSettingsViewSchema, organizationOverridePolicySchema } from './overridable-settings.js';
/**
 * How the kiosk's host search behaves. `SUGGEST` shows typeahead suggestions once
 * `hostLookupMinChars` characters are typed; `FULL_NAME` reveals a host only on an exact
 * full-name match. Uppercase on purpose: it is the Prisma enum's value set, kept in
 * lock-step by `packages/db/src/kiosk-setting-enums.test.ts`.
 */
export const HOST_LOOKUP_MODES = ['SUGGEST', 'FULL_NAME'];
export const hostLookupModeSchema = z.enum(HOST_LOOKUP_MODES);
/** How a self-check-out search matches a visitor's name. Lock-step with the Prisma enum. */
export const CHECK_OUT_MATCH_MODES = ['PARTIAL', 'FULL_NAME'];
export const checkOutMatchModeSchema = z.enum(CHECK_OUT_MATCH_MODES);
/** Characters typed before host suggestions appear (`SUGGEST` mode only). */
export const HOST_LOOKUP_MIN_CHARS_MIN = 2;
export const HOST_LOOKUP_MIN_CHARS_MAX = 8;
export const HOST_LOOKUP_MIN_CHARS_DEFAULT = 3;
export const hostLookupMinCharsSchema = z
    .number()
    .int()
    .min(HOST_LOOKUP_MIN_CHARS_MIN)
    .max(HOST_LOOKUP_MIN_CHARS_MAX);
/** One scope's kiosk behaviour settings — the organization row, a location's override, or the defaults. */
export const kioskSettingValuesSchema = z.object({
    selfCheckOutEnabled: z.boolean(),
    badgePrintingEnabled: z.boolean(),
    hostLookupMode: hostLookupModeSchema,
    hostLookupMinChars: hostLookupMinCharsSchema,
    checkOutMatchMode: checkOutMatchModeSchema,
});
/** The built-in defaults: what a tenant with no organization row resolves to. */
export const KIOSK_SETTING_DEFAULTS = {
    selfCheckOutEnabled: true,
    badgePrintingEnabled: true,
    hostLookupMode: 'SUGGEST',
    hostLookupMinChars: HOST_LOOKUP_MIN_CHARS_DEFAULT,
    checkOutMatchMode: 'PARTIAL',
};
/** `GET|PATCH /api/web/kiosk-settings`: the organization row. */
export const organizationKioskSettingDTOSchema = kioskSettingValuesSchema.merge(organizationOverridePolicySchema);
/** `GET|PATCH /api/web/locations/:locationId/kiosk-settings` and `PUT|DELETE .../kiosk-settings/override`. */
export const locationKioskSettingViewDTOSchema = locationSettingsViewSchema(kioskSettingValuesSchema);
const kioskSettingPatchShape = {
    selfCheckOutEnabled: z.boolean().optional(),
    badgePrintingEnabled: z.boolean().optional(),
    hostLookupMode: hostLookupModeSchema.optional(),
    hostLookupMinChars: hostLookupMinCharsSchema.optional(),
    checkOutMatchMode: checkOutMatchModeSchema.optional(),
};
/**
 * `PATCH /api/web/locations/:locationId/kiosk-settings` — partial edit of the location's
 * override (created as a copy of the organization values first when the location still
 * inherits). `.strict()`: an unknown key is a 400, never silently dropped.
 */
export const updateKioskSettingSchema = z.object(kioskSettingPatchShape).strict();
/** `PATCH /api/web/kiosk-settings` — partial edit of the organization row, including its override toggle. */
export const updateOrganizationKioskSettingSchema = z
    .object({ ...kioskSettingPatchShape, allowLocationOverride: z.boolean().optional() })
    .strict();
//# sourceMappingURL=kiosk-setting.js.map